import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";
import type { AllowedAttribute } from "sanitize-html";

import { colors } from "theme/palette";

import { defaultOptions as defaultSanitizeHtmlOptions } from "components/SanitizeHtml";

import {
  useDealCommunityAmenities,
  useDealCoreData,
  useDealDetails,
  useDealSchedule,
  useDealUnitAmenities,
} from "entities/Deal/sdk";
import {
  usePublicMemoDealDetails,
  useVerifyShareToken,
} from "entities/Memo/publicSdk";
import {
  usePublicDealCommunityAmenities,
  usePublicDealCoreData,
  usePublicDealDetails,
  usePublicDealSchedule,
  usePublicDealSummaryByCategory,
  usePublicDealTeamGroup,
  usePublicDealUnitAmenities,
  usePublicProformaForDeal,
  usePublicRentalData,
} from "entities/Memo/publicSdk";
import { useMemoDealDetails } from "entities/Memo/sdk";
import { useDealSummaryByCategory, useDealTeamGroup } from "entities/Memo/sdk";
import { EAITablesDisplayMode } from "entities/MemoTemplate/constants";
import { useProformaForDeal, useRentalData } from "entities/Proforma/sdk";

export const useDealSummaryByCategoryManager = ({
  token,
  dealId,
  category,
}: {
  token?: string;
  dealId: number;
  category: string;
}) => {
  const publicResponse = usePublicDealSummaryByCategory({ token, category });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealSummaryByCategory
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealSummaryByCategory({
    dealId: token ? undefined : dealId,
    category,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useDealDetailsManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicDealDetails({ token });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealDetails
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealDetails({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useProformaForDealManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicProformaForDeal({ token });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useProformaForDeal
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useProformaForDeal({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useRentalDataManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicRentalData({ token, dealId });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useRentalData
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useRentalData({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useDealScheduleManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicDealSchedule({ token });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealSchedule
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealSchedule({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useDealUnitAmenitiesManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicDealUnitAmenities({ token });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealUnitAmenities
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealUnitAmenities({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useDealCommunityAmenitiesManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicDealCommunityAmenities({ token });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealCommunityAmenities
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealCommunityAmenities({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useDealCoreDataManager = ({
  token,
  dealId,
}: {
  token?: string;
  dealId: number;
}) => {
  const publicResponse = usePublicDealCoreData({ token });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealCoreData
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealCoreData({
    dealId: token ? undefined : dealId,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useDealTeamGroupManager = ({
  token,
  dealId,
  groupName,
}: {
  token?: string;
  dealId: number;
  groupName:
    | "Developer"
    | "Architect"
    | "General Contractor"
    | "Property Manager"
    | "Developer Partner";
}) => {
  const publicResponse = usePublicDealTeamGroup({
    token,
    groupName,
  });

  /**
   * When we have a token this is called from the public memo page (ShareableMemo).
   * In this case we don't want to send a request to useDealTeamGroup
   * because we don't have an Authorization token.
   *  */
  const privateResponse = useDealTeamGroup({
    dealId: token ? undefined : dealId,
    groupName,
  });

  // When we have a token we want to use the public response from the public APIs
  return token ? publicResponse : privateResponse;
};

export const useAITableDisplayMode = ({
  dealId,
  externalToken,
}: {
  dealId: number;
  externalToken?: string;
}) => {
  const params = useParams<{
    memoId: string;
  }>();
  const memoId = Number(params.memoId);

  const { data: memo } = useMemoDealDetails({
    dealId,
    memoId,
  });

  const { isValidating, error: errorToken } = useVerifyShareToken({
    token: externalToken,
  });
  const { data: publicMemo } = usePublicMemoDealDetails({
    token: !isValidating && !errorToken ? externalToken : undefined,
  });

  const tablesDisplayMode = useMemo(() => {
    if (
      [
        memo?.ai_tables_display_mode,
        publicMemo?.ai_tables_display_mode,
      ].includes(EAITablesDisplayMode.NORMAL)
    ) {
      return EAITablesDisplayMode.NORMAL;
    }

    if (
      [
        memo?.ai_tables_display_mode,
        publicMemo?.ai_tables_display_mode,
      ].includes(EAITablesDisplayMode.COMPACT)
    ) {
      return EAITablesDisplayMode.COMPACT;
    }

    return EAITablesDisplayMode.UNSTYLED;
  }, [memo, publicMemo]);

  const sanitizeHtmlOptions = useMemo(() => {
    if (tablesDisplayMode === EAITablesDisplayMode.UNSTYLED) {
      return undefined;
    }

    const tableTags = ["table", "tbody", "thead", "tr", "td", "th"];

    return {
      ...defaultSanitizeHtmlOptions,
      allowedAttributes: {
        ..._.omit(
          defaultSanitizeHtmlOptions.allowedAttributes as Record<
            string,
            Array<AllowedAttribute>
          >,
          ["*"]
        ),
        // Disallow tables from receiving inline style attributes
        ..._.fromPairs(
          (defaultSanitizeHtmlOptions.allowedTags || [])
            .filter((tag) => !tableTags.includes(tag))
            .map((tag) => [tag, ["style", "class", "data-*"]])
        ),
        ..._.fromPairs(tableTags.map((tag) => [tag, ["data-*"]])),
      },
    };
  }, [tablesDisplayMode]);

  const sanitizeHtmlContainerStyles = useMemo(() => {
    if (tablesDisplayMode === EAITablesDisplayMode.UNSTYLED) {
      return undefined;
    }

    const textEntityStyles = {
      textAlign: "left",
      fontSize: "16px",
      lineHeight: "140%",
      fontWeight: 400,
    };

    const tableCellStyles = {
      paddingX: 1,
      paddingY: 0.5,
      border: `1px solid ${colors.blue40}`,
      backgroundColor: colors.white,
      ...textEntityStyles,
    };

    return {
      table: {
        border: `1px solid ${colors.blue40}`,
      },
      ".sanitize-html-wrapper th": {
        ...tableCellStyles,
        fontWeight: 500,
        fontSize: "11px",
        textTransform: "uppercase",
        backgroundColor: colors.blue20,
      },

      ".sanitize-html-wrapper th p": {
        ...textEntityStyles,
        fontSize: "11px",
        fontWeight: 500,
        textTransform: "uppercase",
      },
      ".sanitize-html-wrapper td": tableCellStyles,
      ".sanitize-html-wrapper": {
        ...(tablesDisplayMode === EAITablesDisplayMode.COMPACT
          ? {
              "tr, td, th, table, tbody": {
                padding: 0.5,
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
              },
            }
          : {}),
      },
    };
  }, [tablesDisplayMode]);

  return { sanitizeHtmlOptions, sanitizeHtmlContainerStyles };
};
